import React, { useState, useEffect, useCallback, useRef } from 'react'
import { PageSkeleton } from '../components/PageSkeletons'
import { Link, useLocation } from 'react-router-dom'
import { useToast } from '../context/ToastContext'
import Toggle from '../components/Toggle'
import { outputDestinationsApi } from '../services/api'
import { formatDate } from '../utils/dateUtils'
import { useAuth } from '../context/AuthContext'
import { isReadOnlyUser, READ_ONLY_MESSAGE } from '../utils/readerAccess'
import {
  Server, Plus, Trash2, Edit3, Save, X, Loader2,
  Eye, EyeOff, ArrowLeft, Shield, Key, KeyRound, RefreshCw,
  FolderOpen, CheckCircle2, PlugZap, AlertTriangle,
  ScrollText, ChevronDown, ChevronUp, FileSpreadsheet,
  ExternalLink, Link2, Info,
} from 'lucide-react'

// Both entries spell the protocol out in full - SFTP was previously
// half-expanded ("SSH File Transfer") and FTP not expanded at all.
const PROTOCOLS = [
  { value: 'sftp', label: 'SFTP (SSH File Transfer Protocol)' },
  { value: 'ftp', label: 'FTP (File Transfer Protocol)' },
]

// Destination types this page can configure. The selector was removed in an
// earlier design review when ftp_sftp was the only option and it offered no
// real choice; with a second type it is a genuine decision again.
const DESTINATION_TYPES = [
  { value: 'ftp_sftp', label: 'FTP / SFTP file export' },
  { value: 'azure_sql', label: 'Azure SQL table' },
]

// ─── Azure SQL destination form (create/edit) ───
// Kept as its own component rather than branching inside DestinationForm: the
// two share no fields, and interleaving them would mean editing the FTP form
// that is already in production use.
function AzureSqlForm({ destination, onSave, onCancel, saving, readOnly = false }) {
  const existingConfig = destination?.config || {}
  const hasPassword = !!existingConfig.has_password

  const [name, setName] = useState(destination?.name || '')
  const [server, setServer] = useState(existingConfig.server || '')
  const [database, setDatabase] = useState(existingConfig.database || '')
  const [username, setUsername] = useState(existingConfig.username || '')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [parentTable, setParentTable] = useState(
    existingConfig.parent_table || 'xtract_extraction',
  )
  const [fieldTable, setFieldTable] = useState(
    existingConfig.field_table ?? 'xtract_extraction_field',
  )
  const [egress, setEgress] = useState(null)

  // The address a customer must allow-list differs per deployment, so it comes
  // from the server rather than being written into the UI.
  useEffect(() => {
    let cancelled = false
    outputDestinationsApi
      .connectionInfo()
      .then((res) => { if (!cancelled) setEgress(res.data) })
      .catch(() => { if (!cancelled) setEgress({ configured: false }) })
    return () => { cancelled = true }
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (readOnly) return

    const config = {
      server: server.trim(),
      database: database.trim(),
      username: username.trim(),
      parent_table: parentTable.trim() || 'xtract_extraction',
      field_table: fieldTable.trim(),
    }
    // Only send a new secret if one was actually typed - never wipe a stored
    // password because the field was left blank on an edit.
    if (password) config.password = password

    onSave({ name: name.trim(), type: 'azure_sql', config })
  }

  const canSubmit =
    name.trim() && server.trim() && database.trim() && username.trim() &&
    (hasPassword || password) && parentTable.trim()

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <p className="text-xs text-gray-400">
        Fields marked <span className="text-red-500">*</span> are required.
      </p>

      <div className="rounded-lg border border-blue-200 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/10 px-4 py-3">
        <p className="text-xs text-blue-900 dark:text-blue-200 leading-relaxed">
          Approved results are written to a table in your own Azure SQL
          database. Run <code className="font-mono">azure_sql_destination_schema.sql</code>{' '}
          there first to create the tables, then use Test connection to check
          the login can write to them.
        </p>
        {egress && (
          <p className="text-xs text-blue-900 dark:text-blue-200 mt-2 leading-relaxed">
            {egress.configured
              ? <>Allow this address through your SQL firewall:{' '}
                  <span className="font-mono font-semibold">
                    {(egress.egress_addresses || []).join(', ')}
                  </span></>
              : 'Ask your administrator which outbound address to allow through your SQL firewall.'}
          </p>
        )}
      </div>

      <div>
        <label className="form-label">
          Name <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <input type="text" className="form-input" value={name}
          onChange={(e) => setName(e.target.value)} placeholder="e.g. Orders database"
          required maxLength={100} disabled={readOnly} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="form-label">
            Server <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input type="text" className="form-input" value={server}
            onChange={(e) => setServer(e.target.value)}
            placeholder="yourserver.database.windows.net"
            required disabled={readOnly} />
        </div>
        <div>
          <label className="form-label">
            Database <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input type="text" className="form-input" value={database}
            onChange={(e) => setDatabase(e.target.value)} placeholder="orders"
            required disabled={readOnly} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="form-label">
            Username <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input type="text" className="form-input" value={username}
            onChange={(e) => setUsername(e.target.value)} placeholder="xtract_writer"
            required disabled={readOnly} autoComplete="off" />
        </div>
        <div>
          <label className="form-label">
            Password{' '}
            {hasPassword
              ? <span className="text-gray-400 font-normal">(leave blank to keep)</span>
              : <span className="text-red-500" aria-hidden="true">*</span>}
          </label>
          <div className="relative">
            <input type={showPassword ? 'text' : 'password'} className="form-input pr-10"
              value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder={hasPassword ? '••••••••' : ''}
              disabled={readOnly} autoComplete="new-password" />
            <button type="button" onClick={() => setShowPassword((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              aria-label={showPassword ? 'Hide password' : 'Show password'}>
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="form-label">
            Results table <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input type="text" className="form-input font-mono text-sm" value={parentTable}
            onChange={(e) => setParentTable(e.target.value)}
            placeholder="xtract_extraction" required disabled={readOnly} />
          <p className="mt-1 text-xs text-gray-400">
            One row per extraction. Letters, digits and underscores only.
          </p>
        </div>
        <div>
          <label className="form-label">Fields table</label>
          <input type="text" className="form-input font-mono text-sm" value={fieldTable}
            onChange={(e) => setFieldTable(e.target.value)}
            placeholder="xtract_extraction_field" disabled={readOnly} />
          <p className="mt-1 text-xs text-gray-400">
            One row per field. Leave blank to write only the results table.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary" disabled={!canSubmit || saving || readOnly}>
          {saving ? 'Saving...' : destination ? 'Save changes' : 'Create destination'}
        </button>
      </div>
    </form>
  )
}


// ─── Destination form (create/edit) ───
function DestinationForm({ destination, onSave, onCancel, saving, readOnly = false }) {
  const existingConfig = destination?.config || {}

  const [name, setName] = useState(destination?.name || '')
  const [protocol, setProtocol] = useState(existingConfig.protocol || 'sftp')
  const [host, setHost] = useState(existingConfig.host || '')
  const [port, setPort] = useState(existingConfig.port || (existingConfig.protocol === 'ftp' ? 21 : 22))
  // Standard port for the selected protocol; the Port dropdown offers this or
  // an explicit custom value, so the field can never be left blank.
  const defaultPort = protocol === 'ftp' ? 21 : 22
  const [isCustomPort, setIsCustomPort] = useState(
    !!existingConfig.port &&
    Number(existingConfig.port) !== (existingConfig.protocol === 'ftp' ? 21 : 22)
  )
  const [username, setUsername] = useState(existingConfig.username || '')
  // Password is the default for new destinations and is listed first; an
  // existing destination always keeps its saved choice.
  const [authType, setAuthType] = useState(existingConfig.auth_type || 'password')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [privateKey, setPrivateKey] = useState('')
  const [passphrase, setPassphrase] = useState('')
  const [showPassphrase, setShowPassphrase] = useState(false)
  const [remotePath, setRemotePath] = useState(existingConfig.remote_path || '/')
  const [resetHostKey, setResetHostKey] = useState(false)

  // Masked-secret state from the API: has_password / has_private_key / has_passphrase
  const hasPassword = !!existingConfig.has_password
  const hasPrivateKey = !!existingConfig.has_private_key
  const hasPassphrase = !!existingConfig.has_passphrase
  const hostKeyFingerprint = existingConfig.host_key_fingerprint

  useEffect(() => {
    // Follow the new protocol's standard port unless the user has explicitly
    // chosen a custom one. Number() matters: `port` becomes a STRING once the
    // input is edited, so the old `port === 22` identity checks silently
    // stopped matching and the port could stay on the wrong protocol's value.
    if (!isCustomPort) setPort(protocol === 'ftp' ? 21 : 22)
    // Plain FTP has no key-based auth, so fall back to password there.
    if (protocol === 'ftp' && authType === 'key') setAuthType('password')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocol])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (readOnly) return

    const config = {
      protocol,
      host: host.trim(),
      port: Number(port) || (protocol === 'sftp' ? 22 : 21),
      username: username.trim(),
      auth_type: protocol === 'sftp' ? authType : 'password',
      remote_path: remotePath.trim() || '/',
    }

    // Only send a new secret if the user actually typed one - never wipe
    // an existing stored secret just because the field was left blank.
    if (password) config.password = password
    if (protocol === 'sftp' && authType === 'key') {
      if (privateKey) config.private_key = privateKey
      if (passphrase) config.passphrase = passphrase
    }

    if (resetHostKey) {
      config.host_key_fingerprint = null
      config.host_key_algo = null
    }

    onSave({
      name: name.trim(),
      type: 'ftp_sftp',
      config,
    })
  }

  // NB: plain FTP is always password-based (the protocol effect forces
  // authType back to 'password' when FTP is selected), so the two branches
  // below cover it without a separate FTP case. A previous `(protocol ===
  // 'ftp' || authType === 'password' ? true : true)` clause here was a
  // tautology - both arms returned true - and has been removed.
  const canSubmit =
    name.trim() && host.trim() && username.trim() && remotePath.trim() && port &&
    ((authType === 'password' && (hasPassword || password)) ||
     (authType === 'key' && (hasPrivateKey || privateKey)))

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <p className="text-xs text-gray-400">
        Fields marked <span className="text-red-500">*</span> are required.
      </p>
      {/* Destination Type selector removed per design review: with only one
          configurable type on this page it added no choice, and sat directly
          above Protocol (SFTP/FTP) where it read as a duplicate of the real
          decision. Name now spans the row. */}
      <div>
        <label className="form-label">
          Name <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <input type="text" className="form-input" value={name}
          onChange={(e) => setName(e.target.value)} placeholder="e.g. Finance SFTP Drop"
          required maxLength={100} disabled={readOnly} />
      </div>

      <div>
        <label className="form-label">
          Protocol <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <div className="flex flex-wrap gap-3 mt-1.5">
          {PROTOCOLS.map(p => (
            <button key={p.value} type="button" onClick={() => !readOnly && setProtocol(p.value)}
              disabled={readOnly}
              className={`px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                protocol === p.value
                  ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)]/10 text-[var(--brand-accent)] shadow-sm'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'
              }`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* WHERE the file goes: host, port and path belong together. Remote
          Path was previously paired with Username, which mixed the target
          location with the identity used to reach it. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <label className="form-label">
            Host <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input type="text" className="form-input font-mono text-sm" value={host}
            onChange={(e) => setHost(e.target.value)} placeholder="sftp.example.com"
            required disabled={readOnly} />
        </div>
        <div>
          <label className="form-label">
            Port <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          {/* A dropdown defaulting to the protocol's standard port, because a
              free-text number field could be (and was) left empty, which only
              surfaced as "Please fill in this field" on submit. Custom is still
              offered rather than hard-locking to 22/21 - SFTP on 2222 and FTP
              on 2121 are common enough in the wild to matter. */}
          <select
            className="form-input font-mono text-sm"
            value={isCustomPort ? 'custom' : 'default'}
            onChange={(e) => {
              if (e.target.value === 'custom') {
                setIsCustomPort(true)
              } else {
                setIsCustomPort(false)
                setPort(defaultPort)
              }
            }}
            disabled={readOnly}
          >
            <option value="default">{defaultPort} (default)</option>
            <option value="custom">Custom port…</option>
          </select>
          {isCustomPort && (
            <input type="number" className="form-input font-mono text-sm mt-2"
              value={port} onChange={(e) => setPort(e.target.value)}
              min={1} max={65535} placeholder={String(defaultPort)}
              required disabled={readOnly} autoFocus />
          )}
        </div>
      </div>

      <div>
        <label className="form-label">
          Remote Path <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <input type="text" className="form-input font-mono text-sm" value={remotePath}
          onChange={(e) => setRemotePath(e.target.value)} placeholder="/incoming"
          required disabled={readOnly} />
        <p className="mt-1.5 text-xs text-gray-400 flex items-start gap-1.5">
          <Info className="h-3.5 w-3.5 shrink-0 mt-px" />
          The folder must already exist on the server and be writable by this
          user — Xtract will not create it. Use Test Connection after saving to
          confirm both.
        </p>
      </div>

      {/* WHO connects: username sits with the credentials it belongs to. */}
      <div>
        <label className="form-label">
          Username <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <input type="text" className="form-input text-sm" value={username}
          onChange={(e) => setUsername(e.target.value)} placeholder="username"
          autoComplete="off" required disabled={readOnly} />
      </div>

      {protocol === 'sftp' && (
        <div>
          <label className="form-label">
            Authentication <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <div className="flex flex-wrap gap-3 mt-1.5">
            <button type="button" onClick={() => !readOnly && setAuthType('password')}
              disabled={readOnly}
              className={`px-4 py-2.5 rounded-xl border text-sm font-medium transition-all flex items-center gap-2 ${
                authType === 'password'
                  ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)]/10 text-[var(--brand-accent)] shadow-sm'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'
              }`}>
              <Key className="h-4 w-4" />Password
            </button>
            <button type="button" onClick={() => !readOnly && setAuthType('key')}
              disabled={readOnly}
              className={`px-4 py-2.5 rounded-xl border text-sm font-medium transition-all flex items-center gap-2 ${
                authType === 'key'
                  ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)]/10 text-[var(--brand-accent)] shadow-sm'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'
              }`}>
              <KeyRound className="h-4 w-4" />Private Key
            </button>
          </div>
        </div>
      )}

      {(protocol === 'ftp' || authType === 'password') && (
        <div>
          <label className="form-label">
            Password
            {!hasPassword && <span className="text-red-500" aria-hidden="true"> *</span>}
            {hasPassword && (
              <span className="ml-1.5 text-gray-400 font-normal">(password set - leave blank to keep existing)</span>
            )}
          </label>
          <div className="relative">
            <input type={showPassword ? 'text' : 'password'}
              className="form-input font-mono text-sm pr-10" value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={hasPassword ? 'leave blank to keep existing' : 'password'}
              autoComplete="new-password" disabled={readOnly} />
            {/* Reveal is disabled while the field is empty. On an existing
                destination the input is empty and only shows a placeholder -
                the saved password is never sent to the browser (see
                _mask_destination_config) - so toggling the type appeared to
                do nothing and read as a broken control. The placeholder now
                says so in words rather than showing fake bullets. */}
            <button type="button" onClick={() => setShowPassword((v) => !v)}
              disabled={!password}
              title={
                password
                  ? (showPassword ? 'Hide password' : 'Show password')
                  : 'Nothing to show — the saved password is never sent to the browser'
              }
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed">
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
      )}

      {protocol === 'sftp' && authType === 'key' && (
        <div className="space-y-4">
          <div>
            <label className="form-label">
              Private Key
              {!hasPrivateKey && <span className="text-red-500" aria-hidden="true"> *</span>}
              {hasPrivateKey && (
                <span className="ml-1.5 text-gray-400 font-normal">(key set - leave blank to keep existing)</span>
              )}
            </label>
            <textarea className="form-input font-mono text-xs" rows={6} value={privateKey}
              onChange={(e) => setPrivateKey(e.target.value)}
              placeholder={hasPrivateKey ? '-----BEGIN ... KEY----- (unchanged)' : '-----BEGIN OPENSSH PRIVATE KEY-----\n...'}
              disabled={readOnly} />
          </div>
          <div>
            <label className="form-label">
              Passphrase (optional)
              {hasPassphrase && (
                <span className="ml-1.5 text-gray-400 font-normal">(set - leave blank to keep existing)</span>
              )}
            </label>
            <div className="relative">
              <input type={showPassphrase ? 'text' : 'password'}
                className="form-input font-mono text-sm pr-10" value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                placeholder={hasPassphrase ? 'leave blank to keep existing' : 'optional key passphrase'}
                autoComplete="new-password" disabled={readOnly} />
              {/* Same as the password field: nothing to reveal while empty. */}
              <button type="button" onClick={() => setShowPassphrase((v) => !v)}
                disabled={!passphrase}
                title={
                  passphrase
                    ? (showPassphrase ? 'Hide passphrase' : 'Show passphrase')
                    : 'Nothing to show — the saved passphrase is never sent to the browser'
                }
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed">
                {showPassphrase ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {protocol === 'sftp' && hostKeyFingerprint && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5 text-emerald-500" />Host Key Pinned
              </p>
              <p className="text-[11px] font-mono text-gray-500 dark:text-gray-400 mt-1 break-all">
                {existingConfig.host_key_algo ? `${existingConfig.host_key_algo}: ` : ''}{hostKeyFingerprint}
              </p>
              <p className="text-[11px] text-gray-400 mt-1">
                Pinned on first successful connection (trust-on-first-use). Subsequent connections are verified against this fingerprint.
              </p>
            </div>
            <button type="button" onClick={() => !readOnly && setResetHostKey(v => !v)}
              disabled={readOnly}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border flex items-center gap-1.5 transition-all flex-shrink-0 ${
                resetHostKey
                  ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}>
              <RefreshCw className="h-3.5 w-3.5" />
              {resetHostKey ? 'Will reset on save' : 'Reset host key'}
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
        <button type="button" onClick={onCancel}
          className="btn btn-secondary text-sm px-5">Cancel</button>
        <button type="submit" disabled={readOnly || saving || !canSubmit}
          title={readOnly ? READ_ONLY_MESSAGE : undefined}
          className="btn btn-primary text-sm px-5 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {destination ? 'Save Changes' : 'Create Destination'}
        </button>
      </div>
    </form>
  )
}

// ─── Destination row for list table ───
function DestinationRow({ destination, onClick, onToggle, readOnly = false }) {
  const config = destination.config || {}
  return (
    <tr onClick={onClick}
      className="group cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
      <td className="px-5 py-4">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
            destination.is_active ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-gray-100 dark:bg-gray-800'
          }`}>
            <Server className={`h-4 w-4 ${destination.is_active ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate group-hover:text-[var(--brand-accent)] transition-colors">
              {destination.name}
            </p>
            <p className="text-xs font-mono text-gray-400 truncate" title={`${config.host}:${config.port}`}>
              {config.username}@{config.host}:{config.port}
            </p>
            {/* Flag inert destinations in the list, so you don't have to open
                each one to find the ones that export nothing. */}
            {destination.linked_templates_count === 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 dark:text-amber-400 mt-0.5">
                <AlertTriangle className="h-3 w-3" />
                No templates linked
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="px-5 py-4">
        <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 uppercase">
          {config.protocol}
        </span>
      </td>
      <td className="px-5 py-4">
        <span className="text-xs font-mono text-gray-500 dark:text-gray-400 truncate block max-w-[180px]" title={config.remote_path}>
          {config.remote_path}
        </span>
      </td>
      <td className="px-5 py-4">
        {destination.is_active ? (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />Active
          </span>
        ) : (
          <span className="text-[11px] font-medium text-gray-400">Disabled</span>
        )}
      </td>
      <td className="px-5 py-4">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {destination.created_at ? formatDate(destination.created_at) : '-'}
        </span>
      </td>
      <td className="px-5 py-4 text-right" onClick={e => e.stopPropagation()}>
        <Toggle
          checked={destination.is_active}
          onChange={(e) => { e.stopPropagation(); if (!readOnly) onToggle(destination.id, !destination.is_active) }}
          disabled={readOnly}
          title={readOnly ? READ_ONLY_MESSAGE : (destination.is_active ? 'Disable' : 'Enable')}
        />
      </td>
    </tr>
  )
}

// ─── Destination detail panel ───
// ─── Per-destination delivery log ───
// The aggregate "did last night's exports land?" view. Without this, the only
// export history in the product lived on individual request pages, so
// answering that question meant opening every request one at a time.
function DeliveryLogPanel({ destinationId }) {
  const [deliveries, setDeliveries] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [expandedId, setExpandedId] = useState(null)
  const [loadError, setLoadError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const resp = await outputDestinationsApi.listDeliveriesForDestination(
        destinationId, { limit: 50, status: statusFilter || undefined },
      )
      setDeliveries(resp.data?.deliveries || [])
      setStats(resp.data?.stats || null)
      setLoadError(null)
    } catch (err) {
      // Previously swallowed, which is why a 500 on this endpoint rendered
      // as "no exports yet" - indistinguishable from a genuinely empty log,
      // and it hid a real server error for as long as it existed. An error
      // must never be presented as an empty state.
      setDeliveries([])
      setStats(null)
      setLoadError(
        err?.response?.data?.error ||
        `Could not load delivery history (${err?.response?.status || 'network error'}).`
      )
    } finally {
      setLoading(false)
    }
  }, [destinationId, statusFilter])

  useEffect(() => { load() }, [load])

  // Deliveries complete on a background thread, so a row can still be
  // 'pending' when first rendered. Poll only while that is true, and stop
  // as soon as nothing is pending - same approach as the request page.
  useEffect(() => {
    if (!deliveries.some((d) => d.status === 'pending')) return undefined
    const t = setInterval(load, 5000)
    return () => clearInterval(t)
  }, [deliveries, load])

  const STATUS_STYLES = {
    success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  }

  return (
    <div className="card card-body mt-6">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <ScrollText className="h-4 w-4 text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Delivery History</h2>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs"
          >
            <option value="">All statuses</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
          <button
            onClick={load}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {stats && stats.total > 0 && (
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[
            ['Total', stats.total, 'text-gray-700 dark:text-gray-200'],
            ['Success', stats.success, 'text-emerald-600 dark:text-emerald-400'],
            ['Failed', stats.failed, 'text-red-600 dark:text-red-400'],
            ['Pending', stats.pending, 'text-amber-600 dark:text-amber-400'],
          ].map(([label, value, cls]) => (
            <div key={label} className="rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2">
              <p className="text-[10px] uppercase tracking-wide text-gray-400">{label}</p>
              <p className={`text-lg font-semibold ${cls}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {loadError ? (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10 px-3 py-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-red-800 dark:text-red-300">
                Delivery history unavailable
              </p>
              <p className="text-[11px] text-red-700 dark:text-red-400 mt-0.5 break-words">
                {loadError}
              </p>
              <button onClick={load} className="text-[11px] font-medium text-red-800 dark:text-red-300 underline mt-1">
                Try again
              </button>
            </div>
          </div>
        </div>
      ) : loading && deliveries.length === 0 ? (
        <div className="py-8 text-center">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400 mx-auto" />
        </div>
      ) : deliveries.length === 0 ? (
        <div className="py-8 text-center rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
          <FileSpreadsheet className="h-7 w-7 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {statusFilter ? `No ${statusFilter} deliveries.` : 'No exports have been sent to this destination yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {deliveries.map((d) => {
            const isOpen = expandedId === d.id
            return (
              <div key={d.id} className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
                <button
                  onClick={() => setExpandedId(isOpen ? null : d.id)}
                  className="w-full px-3 py-2.5 flex items-start justify-between gap-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_STYLES[d.status] || STATUS_STYLES.pending}`}>
                        {d.status === 'success' ? 'Success' : d.status === 'failed' ? 'Failed' : 'Pending'}
                      </span>
                      <span className="text-xs font-mono text-gray-700 dark:text-gray-300 truncate">
                        {d.generated_filename || '(no filename)'}
                      </span>
                      {d.request_id != null && (
                        <span className="text-[10px] text-gray-400">Request #{d.request_id}</span>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {d.completed_at ? formatDate(d.completed_at) : d.created_at ? formatDate(d.created_at) : '—'}
                      {d.event ? ` · ${d.event}` : ''}
                      {d.attempt > 1 ? ` · attempt ${d.attempt}` : ''}
                    </p>
                  </div>
                  {isOpen
                    ? <ChevronUp className="h-4 w-4 text-gray-400 shrink-0" />
                    : <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />}
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-gray-800 space-y-2">
                    {d.error_message ? (
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Error</p>
                        {/* Errors can run to 2000 chars; give them room and a
                            scroll rather than clipping them into one line. */}
                        <pre className="text-[11px] text-red-600 dark:text-red-400 whitespace-pre-wrap break-words max-h-48 overflow-auto bg-red-50 dark:bg-red-900/10 rounded p-2 font-mono">
                          {d.error_message}
                        </pre>
                      </div>
                    ) : (
                      <p className="text-[11px] text-gray-400 italic">No errors recorded.</p>
                    )}
                    {d.request_id != null && (
                      <Link
                        reloadDocument
                        to={`/requests/${d.request_id}`}
                        className="inline-block text-[11px] text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Open request #{d.request_id} →
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function DestinationDetailPanel({ destination, onBack, onUpdate, onDelete, onToggle, toast, readOnly = false }) {
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const config = destination.config || {}

  const handleTestConnection = async () => {
    if (readOnly) {
      toast.error('Read-only access', READ_ONLY_MESSAGE)
      return
    }
    setTesting(true)
    setTestResult(null)
    try {
      const resp = await outputDestinationsApi.testConnection(destination.id)
      const data = resp.data || {}
      setTestResult(data)
      if (data.ok) {
        toast.success('Connection OK', 'Xtract connected, wrote a test file and removed it again.')
        // A first-ever SFTP connect pins the host key server-side, so the
        // "Host Key Pinned" badge above is now stale. Deliberately NOT
        // refreshed via onUpdate() - that prop is a raw PUT, and issuing an
        // empty update just to re-read is a write we don't need. The result
        // banner states the pin was established; the badge catches up on the
        // next load of this destination.
      } else {
        toast.error('Connection failed', data.error || 'Could not reach the destination.')
      }
    } catch (err) {
      const msg = err?.response?.data?.error || 'Could not run the connection test.'
      setTestResult({ ok: false, error: msg })
      toast.error('Connection test failed', msg)
    } finally {
      setTesting(false)
    }
  }

  const handleDelete = () => {
    if (readOnly) {
      toast.error('Read-only access', READ_ONLY_MESSAGE)
      return
    }
    setDeleteConfirm({ stage: 'confirm' })
  }

  const performDelete = async (force = false) => {
    if (readOnly) {
      toast.error('Read-only access', READ_ONLY_MESSAGE)
      return
    }
    setDeleting(true)
    try {
      await onDelete(destination.id, { force })
      setDeleteConfirm(null)
      onBack()
    } catch (err) {
      if (err?.response?.status === 409) {
        const linkedCount = err.response?.data?.linked_templates_count ?? null
        setDeleteConfirm({ stage: 'linked', linkedCount })
      } else {
        setDeleteConfirm(null)
      }
    } finally {
      setDeleting(false)
    }
  }

  const handleUpdate = async (data) => {
    if (readOnly) {
      toast.error('Read-only access', READ_ONLY_MESSAGE)
      return
    }
    setSaving(true)
    try {
      await onUpdate(destination.id, data)
      toast.success('Destination Updated', 'The output destination has been updated.')
      setEditing(false)
      onBack()
    } catch (err) {
      toast.error('Update Failed', err?.response?.data?.error || 'Could not update destination.')
    } finally { setSaving(false) }
  }

  if (editing) {
    return (
      <div>
        <button onClick={() => setEditing(false)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to Destination Details
        </button>
        <div className="card card-body">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">Edit Output Destination</h2>
          {/* A destination's type is fixed once created - the two types share no
              fields, so changing it would discard the whole configuration. */}
          {destination?.type === 'azure_sql' ? (
            <AzureSqlForm destination={destination} onSave={handleUpdate} onCancel={() => setEditing(false)} saving={saving} readOnly={readOnly} />
          ) : (
            <DestinationForm destination={destination} onSave={handleUpdate} onCancel={() => setEditing(false)} saving={saving} readOnly={readOnly} />
          )}
        </div>
      </div>
    )
  }

  return (
    <div>
      <button onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" />
        Back to Output Destinations
      </button>

      <div className="card card-body mb-6">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              destination.is_active ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-gray-100 dark:bg-gray-800'
            }`}>
              <Server className={`h-6 w-6 ${destination.is_active ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate">{destination.name}</h1>
                {destination.is_active ? (
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Active
                  </span>
                ) : (
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500">Disabled</span>
                )}
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-500 dark:text-gray-400 font-mono">
                <FolderOpen className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="break-all">{config.username}@{config.host}:{config.port}{config.remote_path}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 uppercase">
                  {config.protocol}
                </span>
                {config.protocol === 'sftp' && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-lg bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400">
                    {config.auth_type === 'key' ? <KeyRound className="h-3 w-3" /> : <Key className="h-3 w-3" />}
                    {config.auth_type === 'key' ? 'Private Key' : 'Password'}
                  </span>
                )}
                {config.host_key_fingerprint && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400">
                    <Shield className="h-3 w-3" />Host Key Pinned
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Toggle
              checked={destination.is_active}
              onChange={(e) => { e.stopPropagation(); if (!readOnly) onToggle(destination.id, !destination.is_active) }}
              disabled={readOnly}
              title={readOnly ? READ_ONLY_MESSAGE : (destination.is_active ? 'Disable' : 'Enable')}
            />
            <button onClick={handleTestConnection} disabled={readOnly || testing}
              title={readOnly ? READ_ONLY_MESSAGE : 'Connect, write a test file, then remove it'}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed">
              {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlugZap className="h-3.5 w-3.5" />}
              {testing ? 'Testing…' : 'Test Connection'}
            </button>
            <button onClick={() => !readOnly && setEditing(true)} disabled={readOnly}
              title={readOnly ? READ_ONLY_MESSAGE : undefined}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed">
              <Edit3 className="h-3.5 w-3.5" />Edit
            </button>
            <button onClick={handleDelete} disabled={readOnly || deleting}
              title={readOnly ? READ_ONLY_MESSAGE : undefined}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed">
              {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* A destination linked to no templates is inert: it exports nothing,
          forever, and nothing used to say so. The count was previously
          computed only when deleting. This is also the "what next?" step
          after creating a destination, which the flow was missing. */}
      {destination.linked_templates_count === 0 && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10 px-4 py-3 mb-6">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                Not linked to any template — nothing will be exported
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                A destination only receives files once it is connected to a template.
                Open a template, go to its <strong>Output Destinations</strong> tab, connect
                this destination, then map its CSV columns.
              </p>
              <Link
                reloadDocument
                to="/templates"
                className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 dark:text-amber-300 underline mt-1.5"
              >
                Go to Templates
                <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {destination.linked_templates_count > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-6">
          <Link2 className="h-3.5 w-3.5" />
          Connected to {destination.linked_templates_count} template
          {destination.linked_templates_count === 1 ? '' : 's'}
        </div>
      )}

      <div className="card card-body">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle2 className="h-4 w-4 text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Configuration</h2>
        </div>
        <dl className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-xs text-gray-400 uppercase tracking-wide mb-1">Host</dt>
            <dd className="font-mono text-gray-700 dark:text-gray-300">{config.host}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-400 uppercase tracking-wide mb-1">Port</dt>
            <dd className="font-mono text-gray-700 dark:text-gray-300">{config.port}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-400 uppercase tracking-wide mb-1">Username</dt>
            <dd className="font-mono text-gray-700 dark:text-gray-300">{config.username}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-400 uppercase tracking-wide mb-1">Remote Path</dt>
            <dd className="font-mono text-gray-700 dark:text-gray-300">{config.remote_path}</dd>
          </div>
        </dl>

        {testResult && (
          <div className={`mt-4 rounded-lg border px-3 py-2.5 ${
            testResult.ok
              ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/10'
              : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10'
          }`}>
            <div className="flex items-start gap-2">
              {testResult.ok
                ? <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
                : <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />}
              <div className="min-w-0 flex-1">
                {testResult.ok ? (
                  <>
                    <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-300">
                      Connection verified over {String(testResult.protocol || '').toUpperCase()}
                    </p>
                    <p className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-0.5">
                      Wrote and removed a test file in <span className="font-mono">{config.remote_path}</span>, so
                      the path exists and the account can write to it.
                      {testResult.host_key_pinned && ' The server\u2019s host key is now pinned.'}
                    </p>
                    {testResult.cleaned_up === false && (
                      <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1">
                        Note: the test file could not be removed afterwards
                        ({testResult.cleanup_error || 'unknown reason'}). The destination works, but you may
                        want to delete <span className="font-mono">{testResult.remote_path}</span> manually.
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-xs font-semibold text-red-800 dark:text-red-300">
                      Connection failed{testResult.error_type ? ` (${testResult.error_type})` : ''}
                    </p>
                    <pre className="text-[11px] text-red-700 dark:text-red-400 mt-1 whitespace-pre-wrap break-words max-h-40 overflow-auto font-mono">
                      {testResult.error}
                    </pre>
                  </>
                )}
              </div>
              <button
                onClick={() => setTestResult(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0"
                title="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      <DeliveryLogPanel destinationId={destination.id} />

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-navy-900/60 backdrop-blur-sm"
            onClick={() => { if (!deleting) setDeleteConfirm(null) }}
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0">
                  <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1 min-w-0">
                  {deleteConfirm.stage === 'linked' ? (
                    <>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        Destination is linked to {deleteConfirm.linkedCount ?? 'one or more'} template{deleteConfirm.linkedCount === 1 ? '' : 's'}
                      </h3>
                      <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        Deleting this destination will immediately stop exporting for {deleteConfirm.linkedCount === 1 ? 'that template' : 'those templates'}.
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        Delete output destination?
                      </h3>
                      <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        This will permanently remove the destination and its saved credentials. This cannot be undone.
                      </p>
                    </>
                  )}
                  <div className="mt-3 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{destination.name}</p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 font-mono truncate mt-0.5">
                      {config.username}@{config.host}:{config.port}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/40 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end gap-2.5">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
              >
                Keep it
              </button>
              <button
                onClick={() => performDelete(deleteConfirm.stage === 'linked')}
                disabled={deleting}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold shadow-sm disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
                {deleteConfirm.stage === 'linked' ? 'Delete anyway' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ===========================================================================
// OUTPUT DESTINATIONS PAGE
// ===========================================================================
export default function OutputDestinations() {
  const location = useLocation()
  const toast = useToast()
  const { user: authUser } = useAuth()
  const isReader = isReadOnlyUser(authUser)
  const [destinations, setDestinations] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [newType, setNewType] = useState('ftp_sftp')
  const [selectedDestination, setSelectedDestination] = useState(null)
  const [saving, setSaving] = useState(false)

  const fetchDestinations = useCallback(async () => {
    try {
      const resp = await outputDestinationsApi.list()
      setDestinations(resp.data.destinations || [])
    } catch (err) {
      toast.error('Load Failed', 'Could not load output destinations.')
    } finally { setLoading(false) }
  }, [toast])

  useEffect(() => { fetchDestinations() }, [fetchDestinations])

  useEffect(() => {
    if (location.pathname !== '/output-destinations') return
    if (!location.state?.fromSidebar || !location.state?.navClickAt) return

    setShowForm(false)
    setSelectedDestination(null)
  }, [location.pathname, location.state])

  // Deep link: /output-destinations?destination=<id> opens that destination's
  // detail panel directly, so the template tab's "Full delivery history" link
  // lands on the right destination's log instead of the bare list.
  // Honoured once per URL. `destinations` is a fresh array on every fetch, so
  // without this guard clicking "Back to Output Destinations" would reopen the
  // panel the next time the list refreshed, trapping the user on the detail
  // view while ?destination= stayed in the address bar.
  const handledDeepLinkRef = useRef(null)
  useEffect(() => {
    const wanted = new URLSearchParams(location.search).get('destination')
    if (!wanted) {
      handledDeepLinkRef.current = null
      return
    }
    if (destinations.length === 0) return
    if (handledDeepLinkRef.current === wanted) return
    handledDeepLinkRef.current = wanted

    const match = destinations.find((d) => String(d.id) === String(wanted))
    if (match) {
      setShowForm(false)
      setSelectedDestination(match)
    } else {
      // Linked from a template whose destination has since been deleted.
      toast.error(
        'Destination not found',
        'That output destination no longer exists.',
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, destinations])

  const handleCreate = async (data) => {
    if (isReader) {
      toast.error('Read-only access', READ_ONLY_MESSAGE)
      return
    }
    setSaving(true)
    try {
      const resp = await outputDestinationsApi.create(data)
      toast.success('Destination Created', 'Now test the connection and link it to a template.')
      setShowForm(false)
      await fetchDestinations()
      // Land on the new destination rather than back on the table. Creating a
      // destination is only step one - it exports nothing until it is tested
      // and linked to a template, and both of those live on the detail panel.
      // Dropping the user back on a list hid the entire rest of the journey.
      const created = resp?.data
      if (created?.id) {
        setSelectedDestination({ ...created, linked_templates_count: 0 })
      }
    } catch (err) {
      toast.error('Create Failed', err?.response?.data?.error || 'Could not create destination.')
    } finally { setSaving(false) }
  }

  const handleUpdate = async (id, data) => {
    return outputDestinationsApi.update(id, data)
  }

  const handleDelete = async (id, { force = false } = {}) => {
    if (isReader) {
      toast.error('Read-only access', READ_ONLY_MESSAGE)
      return
    }
    try {
      await outputDestinationsApi.delete(id, { force })
      toast.success('Destination Deleted', 'The output destination has been removed.')
      fetchDestinations()
    } catch (err) {
      if (err?.response?.status === 409) {
        throw err
      }
      toast.error('Delete Failed', err?.response?.data?.error || 'Could not delete destination.')
      throw err
    }
  }

  const handleToggle = async (id, isActive) => {
    if (isReader) {
      toast.error('Read-only access', READ_ONLY_MESSAGE)
      return
    }
    try {
      await outputDestinationsApi.update(id, { is_active: isActive })
      fetchDestinations()
      if (selectedDestination?.id === id) {
        setSelectedDestination(prev => ({ ...prev, is_active: isActive }))
      }
    } catch (err) {
      toast.error('Toggle Failed', 'Could not update destination status.')
    }
  }

  if (loading) {
    return (
      <PageSkeleton />
    )
  }

  // ─── Detail view ───
  if (selectedDestination) {
    return (
      <div className="w-full">
        <DestinationDetailPanel
          destination={selectedDestination}
          onBack={() => { setSelectedDestination(null); fetchDestinations() }}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onToggle={handleToggle}
          toast={toast}
          readOnly={isReader}
        />
      </div>
    )
  }

  // ─── Create form view ───
  if (showForm) {
    return (
      <div className="w-full">
        <button onClick={() => setShowForm(false)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to Output Destinations
        </button>
        <div className="card card-body">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">Create Output Destination</h2>
          <div className="mb-6">
            <label className="form-label">Destination type</label>
            <select className="form-input" value={newType}
              onChange={(e) => setNewType(e.target.value)} disabled={isReader}>
              {DESTINATION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          {newType === 'azure_sql' ? (
            <AzureSqlForm onSave={handleCreate} onCancel={() => setShowForm(false)} saving={saving} readOnly={isReader} />
          ) : (
            <DestinationForm onSave={handleCreate} onCancel={() => setShowForm(false)} saving={saving} readOnly={isReader} />
          )}
        </div>
      </div>
    )
  }

  // ─── List view ───
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Output Destinations</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Configure FTP/SFTP destinations to automatically export approved request data as CSV files.
          </p>
        </div>
        <button onClick={() => !isReader && setShowForm(true)}
          disabled={isReader}
          title={isReader ? READ_ONLY_MESSAGE : undefined}
          className={`btn flex items-center gap-1.5 text-sm ${isReader ? 'opacity-50 cursor-not-allowed bg-gray-300 dark:bg-gray-700 text-gray-500' : 'btn-primary'}`}>
          <Plus className="h-4 w-4" />
          Add Destination
        </button>
      </div>

      {destinations.length === 0 ? (
        <div className="card card-body flex flex-col items-center justify-center py-16 text-gray-400">
          <Server className="h-10 w-10 mb-3 opacity-40" />
          <p className="text-sm font-medium">No output destinations yet</p>
          <p className="text-xs mt-1">Add an FTP/SFTP destination to start exporting approved request data automatically.</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50/50 dark:bg-gray-800/30 border-b border-gray-100 dark:border-gray-800">
                <th className="px-5 py-3">Destination</th>
                <th className="px-5 py-3">Protocol</th>
                <th className="px-5 py-3">Remote Path</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Created</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {destinations.map(dest => (
                <DestinationRow key={dest.id} destination={dest}
                  onClick={() => setSelectedDestination(dest)}
                  onToggle={handleToggle}
                  readOnly={isReader} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
